import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

// SVG Icons
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MinusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1m-17 4h14m-7 4h7m-14 4h14" />
  </svg>
);

const SortIcon = ({ direction }) => (
  <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    {direction === 'asc' ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7-7m0 0l-7 7m7-7v18" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 10l-7 7m0 0l-7-7m7 7V3" />
    )}
  </svg>
);

const MeetingReport = () => {
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openMeetingId, setOpenMeetingId] = useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();

  // Base URL for QR code images (adjust as needed)
  const QR_CODE_BASE_URL = "https://your-api-base-url.com/"; // Replace with your actual base URL

  // Fetch meetings
  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        throw new Error("Phone number not found in local storage");
      }

      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      console.log("Fetched meetings data:", result);
      if (result.success) {
        setMeetings(result.data);
      } else {
        throw new Error(result.message || "Failed to fetch meetings");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching meetings:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key && prevConfig.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Sort meetings
  const sortedMeetings = React.useMemo(() => {
    if (!sortConfig.key) return meetings;

    const sorted = [...meetings].sort((a, b) => {
      if (sortConfig.key === 'sno') {
        return sortConfig.direction === 'asc' ? a.id - b.id : b.id - a.id;
      }
      if (sortConfig.key === 'time') {
        const aTime = a.time ? new Date(a.time.replace(" ", "T")).getTime() : 0;
        const bTime = b.time ? new Date(b.time.replace(" ", "T")).getTime() : 0;
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }
      if (sortConfig.key === 'invite_code') {
        const aCode = a.attendees && a.attendees[0]?.invite_code || '';
        const bCode = b.attendees && b.attendees[0]?.invite_code || '';
        return sortConfig.direction === 'asc'
          ? aCode.localeCompare(bCode)
          : bCode.localeCompare(aCode);
      }
      return 0;
    });
    return sorted;
  }, [meetings, sortConfig]);

  // Handle search and filter
  const filteredMeetings = sortedMeetings.filter((meeting) => {
    const title = meeting.title || "";
    const purpose = meeting.purpose || "";
    const status = meeting.status || "";
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purpose.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  // Toggle dropdown
  const toggleDropdown = (meetingId) => {
    setOpenMeetingId(openMeetingId === meetingId ? null : meetingId);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!selectedMeetingId) {
      alert("Please select a meeting to export.");
      return;
    }

    const selectedMeeting = meetings.find((meeting) => String(meeting.id) === String(selectedMeetingId));
    if (!selectedMeeting) {
      alert("Selected meeting not found.");
      return;
    }

    // Prepare CSV headers
    const headers = [
      "S.No",
      "ID",
      "Title",
      "Purpose",
      "Time",
      "Description",
      "Status",
      "Created By",
      "Created At",
      "Is Public",
      "Attendee Name",
      "Attendee Email",
      "Attendee Phone",
      "Attendee Role",
      "Attended",
      "Attended Time",
      "Invite Code",
      "Unique Code",
      "QR Code Path",
      "Ticket ID",
      "Payment ID",
      "Order ID",
      "Pincode",
      "Address",
      "Location"
    ];
    const csvRows = [headers.join(",")];

    // Prepare attendee rows
    const attendees = selectedMeeting.attendees || [];
    if (attendees.length === 0) {
      const row = [
        "1",
        selectedMeeting.id || "N/A",
        `"${(selectedMeeting.title || "").replace(/"/g, '""')}"`,
        `"${(selectedMeeting.purpose || "").replace(/"/g, '""')}"`,
        `"${selectedMeeting.time ? new Date(selectedMeeting.time.replace(" ", "T")).toLocaleString() : "N/A"}"`,
        `"${(selectedMeeting.description || "").replace(/"/g, '""')}"`,
        selectedMeeting.status || "N/A",
        `"${(selectedMeeting.created_by || "N/A").replace(/"/g, '""')}"`,
        `"${selectedMeeting.created_at ? new Date(selectedMeeting.created_at.replace(" ", "T")).toLocaleString() : "N/A"}"`,
        selectedMeeting.is_public ? "Yes" : "No",
        "", "", "", "", "", "", "", "", "", "", "", "", "",
        `"${(selectedMeeting.location || "N/A").replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(","));
    } else {
      attendees.forEach((attendee, attIndex) => {
        const row = [
          attIndex + 1,
          selectedMeeting.id || "N/A",
          `"${(selectedMeeting.title || "").replace(/"/g, '""')}"`,
          `"${(selectedMeeting.purpose || "").replace(/"/g, '""')}"`,
          `"${selectedMeeting.time ? new Date(selectedMeeting.time.replace(" ", "T")).toLocaleString() : "N/A"}"`,
          `"${(selectedMeeting.description || "").replace(/"/g, '""')}"`,
          selectedMeeting.status || "N/A",
          `"${(selectedMeeting.created_by || "N/A").replace(/"/g, '""')}"`,
          `"${selectedMeeting.created_at ? new Date(selectedMeeting.created_at.replace(" ", "T")).toLocaleString() : "N/A"}"`,
          selectedMeeting.is_public ? "Yes" : "No",
          `"${(attendee.name || "Unknown").replace(/"/g, '""')}"`,
          `"${(attendee.email || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.phone || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.role || "User").replace(/"/g, '""')}"`,
          attendee.scan_status ? "Attended" : "Not Attended",
          `"${attendee.scan_status && attendee.scan_time ? new Date(attendee.scan_time.replace(" ", "T")).toLocaleString() : "-"}"`,
          `="${(attendee.invite_code || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.unique_code || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.qr_code_path || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.ticket_id || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.payment_id || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.order_id || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.pincode || "N/A").replace(/"/g, '""')}"`,
          `"${(attendee.address || "N/A").replace(/"/g, '""')}"`,
          `"${(selectedMeeting.location || "N/A").replace(/"/g, '""')}"`
        ];
        csvRows.push(row.join(","));
      });
    }

    // Generate and download CSV
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `meeting_report_${selectedMeeting.title || 'meeting'}_${selectedMeetingId}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Calculate attendee counts
  const getAttendeeCounts = (attendees) => {
    const total = attendees.length;
    const attended = attendees.filter(attendee => attendee.scan_status).length;
    return { total, attended };
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} font-sans`}>
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-800 dark:to-cyan-800 text-white p-4 shadow-xl fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-4 md:hidden focus:outline-none hover:bg-teal-700 dark:hover:bg-teal-900 p-2 rounded-full transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold tracking-tight">Event Manager</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-teal-700 dark:hover:bg-teal-900 transition-all duration-300"
            >
              {isDarkMode ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-red-500 rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition-all duration-300 text-sm font-medium shadow-md hover:scale-105"
            >
              Back
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <div
        className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20 shadow-lg`}
      >
        {/* Sidebar button for Dashboard */}
        <button
          className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Dashboard"
          onClick={() => navigate('/dashboard')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
        </button>
        {/* Sidebar button for Create Meeting */}
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Create Meeting"
          onClick={() => navigate('/create-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
        </button>
        {/* Sidebar button for View Meetings */}
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="View Meetings"
          onClick={() => navigate('/view-meetings')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
        </button>
        {/* Sidebar button for Send Invites */}
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invites"
          onClick={() => navigate('/send-invites')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
        </button>
        {/* Sidebar button for Search Meetings */}
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Search Meetings"
          onClick={() => navigate('/search-form')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
        </button>
        {/* Sidebar button for Send Invite Without Pay */}
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invite Without Pay"
          onClick={() => navigate('/adminsend')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
        </button>
        {/* Sidebar button for Restrict Meeting */}
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Restrict Meeting"
          onClick={() => navigate('/restrict-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="pt-20 md:pl-24 p-6 max-w-7xl mx-auto transition-all duration-500">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white animate-fade-in">Meeting Reports</h1>
          <button
            onClick={fetchMeetings}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-800 transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl hover:scale-105"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-xl border border-red-200 dark:border-red-700 text-sm animate-slide-in shadow-md">
            {error}
            <button
              onClick={fetchMeetings}
              className="ml-3 text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-200 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl animate-fade-in border border-gray-200 dark:border-gray-700">
          {/* Search, Filter, and CSV Export */}
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
            <div className="relative w-full sm:w-96">
              <input
                type="text"
                placeholder="Search by title or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400 dark:text-white shadow-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center">
                <SearchIcon />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-12 pr-8 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400 dark:text-white appearance-none shadow-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                </select>
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center">
                  <FilterIcon />
                </div>
              </div>
              <div className="relative">
                <select
                  value={selectedMeetingId}
                  onChange={(e) => setSelectedMeetingId(e.target.value)}
                  className="pl-12 pr-8 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm transition-all duration-300 hover:border-teal-400 dark:text-white appearance-none shadow-sm"
                >
                  <option value="">Select a meeting to export</option>
                  {filteredMeetings.map((meeting) => (
                    <option key={meeting.id} value={meeting.id}>
                      {(meeting.title || meeting.purpose || "Untitled Meeting")} ({meeting.time ? new Date(meeting.time.replace(" ", "T")).toLocaleDateString() : "N/A"})
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center">
                  <DownloadIcon />
                </div>
              </div>
              <button
                onClick={exportToCSV}
                className="px-5 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-800 flex items-center transition-all duration-300 text-sm font-medium shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!selectedMeetingId}
              >
                <DownloadIcon />
                Export to CSV
              </button>
            </div>
          </div>

          {/* Meetings Table */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm">Loading meetings...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-teal-50 dark:bg-teal-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      <button onClick={() => handleSort('sno')} className="flex items-center">
                        S.No
                        {sortConfig.key === 'sno' && <SortIcon direction={sortConfig.direction} />}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Purpose</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      <button onClick={() => handleSort('time')} className="flex items-center">
                        Time
                        {sortConfig.key === 'time' && <SortIcon direction={sortConfig.direction} />}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Attendees</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      <button onClick={() => handleSort('invite_code')} className="flex items-center">
                        Invite Code
                        {sortConfig.key === 'invite_code' && <SortIcon direction={sortConfig.direction} />}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredMeetings.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-6 text-center text-gray-500 dark:text-gray-400 text-sm font-medium">No meetings found</td>
                    </tr>
                  ) : (
                    filteredMeetings.map((meeting, index) => {
                      const { total, attended } = getAttendeeCounts(meeting.attendees || []);
                      return (
                        <React.Fragment key={meeting.id}>
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{index + 1}</td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{meeting.title || "Untitled"}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{meeting.purpose || "N/A"}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{meeting.location || "N/A"}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{meeting.time ? new Date(meeting.time.replace(" ", "T")).toLocaleString() : "N/A"}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 capitalize">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${meeting.status === 'scheduled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                meeting.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  meeting.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                {meeting.status || "N/A"}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                              {total} Total / {attended} Attended
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                              {(meeting.attendees && meeting.attendees[0]?.invite_code) || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => toggleDropdown(meeting.id)}
                                className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 transition-all duration-300 transform hover:scale-110"
                              >
                                {openMeetingId === meeting.id ? <MinusIcon /> : <PlusIcon />}
                              </button>
                            </td>
                          </tr>
                          {openMeetingId === meeting.id && (
                            <tr>
                              <td colSpan="9" className="px-6 py-4 bg-gray-50 dark:bg-gray-700">
                                <div className="animate-slide-down">
                                  <div className="mb-6">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Meeting Details</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                                      <div>
                                        <p><strong>Title:</strong> {meeting.title || "Untitled"}</p>
                                        <p><strong>Purpose:</strong> {meeting.purpose || "N/A"}</p>
                                        <p><strong>Location:</strong> {meeting.location || "N/A"}</p>
                                        <p><strong>Time:</strong> {meeting.time ? new Date(meeting.time.replace(" ", "T")).toLocaleString() : "N/A"}</p>
                                      </div>
                                      <div>
                                        <p><strong>Description:</strong> {meeting.description || "N/A"}</p>
                                        <p><strong>Created By:</strong> {meeting.created_by || "N/A"}</p>
                                        <p><strong>Created At:</strong> {meeting.created_at ? new Date(meeting.created_at.replace(" ", "T")).toLocaleString() : "N/A"}</p>
                                        <p><strong>Public:</strong> {meeting.is_public ? "Yes" : "No"}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Attendees</h4>
                                  {(meeting.attendees || []).length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No attendees found</p>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                        <thead className="bg-teal-50 dark:bg-teal-900/50">
                                          <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">S.No</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Name</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Email</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Phone</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Role</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Invite Code</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Unique Code</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">QR Code</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Attended</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">Attended Time</th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                          {(meeting.attendees || []).map((attendee, attIndex) => (
                                            <tr key={attendee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200">
                                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{attIndex + 1}</td>
                                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{attendee.name || "Unknown"}</td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{attendee.email || "N/A"}</td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{attendee.phone || "N/A"}</td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 capitalize">{attendee.role || "User"}</td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{attendee.invite_code || "N/A"}</td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{attendee.unique_code || "N/A"}</td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                                {attendee.qr_code_path ? (
                                                  <img
                                                    src={`${ApiURl}/${attendee.qr_code_path}`}
                                                    alt={`QR Code for ${attendee.name || "Attendee"}`}
                                                    className="w-16 h-16 object-contain"
                                                    onError={(e) => { e.target.src = "/fallback-qr.png"; }}
                                                  />
                                                ) : (
                                                  "N/A"
                                                )}
                                              </td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${attendee.scan_status ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                                  }`}>
                                                  {attendee.scan_status ? 'Attended' : 'Not Attended'}
                                                </span>
                                              </td>
                                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                                {attendee.scan_time
                                                  ? new Date(attendee.scan_time.replace(" ", "T")).toLocaleString()
                                                  : '-'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Tailwind Animation Styles */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
        .animate-slide-down {
          animation: slideDown 0.3s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MeetingReport;